/**
 * AdminParasutMapping — Paraşüt (muhasebe SaaS) eşleme ekranı.
 * Route: /admin/parasut-mapping · Roles: admin, superadmin
 *
 * Üç sekme:
 *   Kurs eşleme  — kurs ↔ Paraşüt ürün/hizmet (`product.id`).
 *   Banka/Kasa   — banka/kasa/POS hesabı ↔ Paraşüt hesabı (`account.id`),
 *                  BU ortam (staging/production) için; para birimi uyuşmazsa
 *                  409 döner, admin açık onayla (`force`) yine de eşleyebilir.
 *   Outbox       — durum başına satır sayısı + `blocked` satırların listesi
 *                  ("sırada ne var" kuyruğu — eşleme bekleyen kurs/banka).
 *
 * Backend: backend/routes/parasut_admin.py (bkz. modül docstring'i).
 * PARASUT_ENABLED bayrağı KAPALIYKEN (bugün prod varsayılanı) eşleme CRUD'u
 * ve outbox uçları normal çalışır — yalnızca Paraşüt'e giden iki salt-okunur
 * katalog ucu (`products/remote`, `accounts/remote`) 503 döner. Bu yüzden
 * eşleme ID'si HER ZAMAN elle girilebilir; "Paraşüt'ten seç" yalnızca bir
 * kolaylıktır, zorunlu değildir.
 *
 * i18n SAPMASI (bilinçli, görev talimatınca): metinler bu dosya içinde yerel
 * bir TR/EN sözlükte tutuluyor, `contexts/LanguageContext.js`'e DOKUNULMADI
 * (paralel bir tur o dosyada çalışıyordu — çakışma riski). Yalnızca dil SEÇİMİ
 * (`lang`) `useLanguage()`'ten okunuyor. Kenar çubuğu menü etiketi
 * (`admin.nav.parasutMapping`) bu yüzden LanguageContext'te TANIMLI DEĞİL —
 * bkz. `src/lib/adminNav.js` üstündeki not.
 *
 * Pasif buton + gizli hata tuzağı (önceki turda düzeltildi): zorunlu alan
 * boşken kaydet/eşle butonları DEVRE DIŞI BIRAKILMAZ — tıklanınca doğrulama
 * çalışır ve `role="alert"` hata metni gösterilir. Böylece hata metninin
 * görünürlüğü butonun disabled durumuna BAĞLI DEĞİLDİR.
 */

import { useState, useEffect, useRef } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, LoadingState, EmptyState } from '../components/admin';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../contexts/ToastContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import { fmtTRY } from '../utils/formatCurrency';
import {
  useParasutConfig,
  useParasutRemoteProducts,
  useParasutRemoteAccounts,
  useCourseMappings,
  useSetCourseMapping,
  useDeleteCourseMapping,
  useBankMappings,
  useSetBankMapping,
  useDeleteBankMapping,
  useOutboxSummary,
  useOutboxBlocked,
  useOutboxList,
  useRetryOutboxItem,
  useResolveNeedsVerification,
  useResolveUnknown,
  useInvoiceItemMapping,
  useSetInvoiceItemMapping,
} from '../api/admin/parasutMapping';

// ── Yerel TR/EN sözlük (bkz. dosya başı i18n sapma notu) ─────────────────────

const STRINGS = {
  tr: {
    'page.title': 'Paraşüt Eşleme',
    'page.subtitle': 'Kurs ↔ ürün, banka/kasa ↔ hesap eşlemeleri ve outbox kuyruğu özeti',

    'config.title': 'Bağlantı durumu',
    'config.enabled': 'Entegrasyon',
    'config.enabledOn': 'Açık',
    'config.enabledOff': 'Kapalı',
    'config.host': 'Ortam',
    'config.hostProd': 'Production',
    'config.hostStaging': 'Staging',
    'config.credentials': 'Kimlik bilgileri',
    'config.credentialsOk': 'Tanımlı',
    'config.credentialsMissing': 'Eksik',
    'config.disabledHint': 'Paraşüt bağlantısı kapalı; eşleme ID\'lerini elle girebilirsiniz. Bu ekrandaki kaydetme/silme işlemleri bağlantı kapalıyken de normal çalışır — yalnızca "Paraşüt\'ten seç" listesi kapalıdır.',

    'tabs.courses': 'Kurs Eşleme',
    'tabs.banks': 'Banka/Kasa Eşleme',
    'tabs.outbox': 'Fatura Kuyruğu',

    'courses.onlyUnmapped': 'Yalnızca eşlenmemiş',
    'courses.empty.title': 'Kurs bulunamadı',
    'courses.empty.desc': 'Listelenecek kurs yok.',
    'courses.mapped': 'Eşlendi',
    'courses.unmapped': 'Eşlenmedi',
    'courses.inputLabel': 'Paraşüt ürün ID',
    'courses.inputPlaceholder': 'ör. 12345678',
    'courses.save': 'Kaydet',
    'courses.saving': 'Kaydediliyor…',
    'courses.delete': 'Eşlemeyi kaldır',
    'courses.deleting': 'Kaldırılıyor…',
    'courses.requiredError': 'Paraşüt ürün ID zorunludur.',
    'courses.saveSuccess': 'Kurs eşlemesi kaydedildi.',
    'courses.deleteSuccess': 'Kurs eşlemesi kaldırıldı.',
    'courses.pick': 'Paraşüt\'ten seç',
    'courses.pickClose': 'Kapat',

    // Tur 2 (2026-09-29) — genel "Eğitim Ödemesi" ürün eşlemesi. Backend ucu
    // (GET/PUT /admin/parasut/invoice-item-mapping) Tur 3'te HAZIR doğrulandı
    // — bkz. api/admin/parasutMapping.js.
    'invoiceItem.title': 'Eğitim Ödemesi (Genel Ürün)',
    'invoiceItem.desc': 'Kurs bazlı bir eşleme yoksa (veya taksitsiz/bare fatura satırlarında) Paraşüt\'e gönderilecek GENEL ürün/hizmet.',
    'invoiceItem.notConfigured': 'Eğitim ödemesi eşlemesi yüklenemedi. Lütfen sayfayı yenileyin.',
    'invoiceItem.mappedTo': 'Seçili ürün',
    'invoiceItem.unmapped': 'Seçili değil',
    'invoiceItem.inputLabel': 'Paraşüt ürün ID',
    'invoiceItem.inputPlaceholder': 'ör. 12345678',
    'invoiceItem.save': 'Kaydet',
    'invoiceItem.saving': 'Kaydediliyor…',
    'invoiceItem.requiredError': 'Paraşüt ürün ID zorunludur.',
    'invoiceItem.saveSuccess': 'Eğitim ödemesi ürünü kaydedildi.',
    'invoiceItem.pick': 'Paraşüt\'ten seç',

    'banks.empty.title': 'Banka/kasa bulunamadı',
    'banks.empty.desc': 'Listelenecek banka/kasa hesabı yok.',
    'banks.type': 'Hesap türü',
    'banks.currency': 'Para birimi',
    'banks.mappedTo': 'Paraşüt hesabı',
    'banks.unmapped': 'Eşlenmedi',
    'banks.inputLabel': 'Paraşüt hesap ID',
    'banks.inputPlaceholder': 'ör. 87654321',
    'banks.save': 'Eşle',
    'banks.saving': 'Eşleniyor…',
    'banks.delete': 'Eşlemeyi kaldır',
    'banks.deleting': 'Kaldırılıyor…',
    'banks.requiredError': 'Paraşüt hesap ID zorunludur.',
    'banks.saveSuccess': 'Banka/kasa eşlemesi kaydedildi.',
    'banks.deleteSuccess': 'Banka/kasa eşlemesi kaldırıldı.',
    'banks.forceBtn': 'Yine de eşle',
    'banks.forcing': 'Eşleniyor…',
    'banks.pick': 'Paraşüt\'ten seç',
    'banks.pickClose': 'Kapat',

    'remote.disabled': 'Paraşüt bağlantısı kapalı; ID\'yi elle girebilirsiniz.',
    'remote.loading': 'Paraşüt kataloğu yükleniyor…',
    'remote.error': 'Paraşüt kataloğu yüklenemedi.',
    'remote.empty': 'Kayıt bulunamadı.',
    'remote.use': 'Bunu kullan',
    'remote.loadMore': 'Daha fazla yükle',
    'remote.loadingMore': 'Yükleniyor…',

    'outbox.summaryTitle': 'Durum özeti',
    'outbox.blockedTitle': 'Bloke satırlar ("sırada ne var")',
    'outbox.filterAll': 'Tümü',

    // Tur 6 (elle test bulguları) — teknik değer -> anlaşılır etiket.
    // `entityTypeLabel`/`outboxStatusLabel`/`operationLabel`/`blockReasonLabel`
    // bu anahtarları `labelOr` ile OKUR (bulunamazsa ham değere düşer,
    // uydurma yapmaz).
    'entity.sales_invoice': 'Satış faturası',
    'entity.contact': 'Cari (öğrenci)',
    'entity.payment': 'Tahsilat',
    'entity.product': 'Ürün',
    'entity.e_document': 'E-belge',

    'status.pending': 'Gönderilmeyi bekliyor',
    'status.in_progress': 'Gönderiliyor',
    'status.awaiting_job': "Paraşüt işliyor",
    'status.done': 'Tamamlandı',
    'status.failed': 'Hata',
    'status.blocked': 'Önceki adımı bekliyor',
    'status.needs_verification': 'Kontrol gerekli',
    'status.unknown': 'Durum belirsiz',
    'status.cancelled': 'İptal edildi',

    'operation.create': 'Oluştur',
    'operation.update': 'Güncelle',
    'operation.collect': 'Tahsil et',
    'operation.officialize': 'Resmileştir',
    'operation.cancel': 'İptal et',

    'blockReason.missing_product_mapping': 'Ürün eşlemesi eksik',
    'blockReason.missing_account_mapping': 'Hesap eşlemesi eksik',
    'blockReason.missing_tax_number': "Öğrencinin TC'si eksik",
    'blockReason.depends_on': 'Önceki adımı bekliyor',
    'outbox.empty': 'Bu filtrede bloke satır yok.',
    'outbox.refresh': 'Yenile',
    'outbox.col.entity': 'Kayıt',
    'outbox.col.operation': 'İşlem',
    'outbox.col.reason': 'Gerekçe',
    'outbox.col.created': 'Oluşturulma',

    // Tur 2 (2026-09-29) — Outbox genel kuyruk (durum filtreli, retry/resolve)
    'outbox.list.title': 'Genel Kuyruk',
    'outbox.list.filterLabel': 'Durum',
    'outbox.list.filterAll': 'Tümü',
    'outbox.list.entityFilterLabel': 'Kayıt türü',
    'outbox.list.entityFilterAll': 'Tümü',
    'outbox.list.empty': 'Bu filtrede kayıt yok.',
    'outbox.list.col.status': 'Durum',
    'outbox.list.retryBtn': 'Tekrar Gönder',
    'outbox.list.retryNoteLabel': 'Not (zorunlu)',
    'outbox.list.retryNoteRequired': 'Not zorunludur.',
    'outbox.list.retrySuccess': 'Yeniden gönderildi.',
    'outbox.list.resolveBtn': 'Çöz',
    'outbox.list.resolveTitleNeedsVerification': 'Doğrulama sonucu',
    'outbox.list.resolveTitleUnknown': 'Durumu çöz',
    'outbox.list.resolutionLabel': 'Sonuç',
    'outbox.list.resolutionRequired': 'Sonuç seçimi zorunludur.',
    'outbox.list.resolutionPending': 'Beklemede (pending)',
    'outbox.list.resolutionDone': 'Tamamlandı (done)',
    'outbox.list.resolutionFailed': 'Başarısız (failed)',
    'outbox.list.parasutIdLabel': 'Paraşüt Fatura ID',
    'outbox.list.parasutIdRequired': 'Paraşüt Fatura ID zorunludur.',
    'outbox.list.doubleInvoiceWarning': 'Dikkat: yanlışlıkla yeniden gönderme Paraşüt\'te ÇİFT fatura oluşturabilir. Devam etmeden önce Paraşüt panelinde bu tahsilat için fatura olmadığını doğrulayın.',
    'outbox.list.confirmNotInParasut': 'Paraşüt panelinde bu tahsilat için fatura OLMADIĞINI kontrol ettim.',
    'outbox.list.confirmNotInParasutRequired': 'Devam etmeden önce bu onayı işaretlemelisiniz.',
    'outbox.list.noteRequired': 'Not zorunludur.',
    'outbox.list.resolveSuccess': 'Çözüldü.',
    'outbox.list.submit': 'Gönder',

    'common.loading': 'Yükleniyor…',
    'common.cancel': 'Vazgeç',
    'common.retry': 'Tekrar dene',
  },
  en: {
    'page.title': 'Paraşüt Mapping',
    'page.subtitle': 'Course ↔ product, bank/cash ↔ account mappings and outbox queue summary',

    'config.title': 'Connection status',
    'config.enabled': 'Integration',
    'config.enabledOn': 'Enabled',
    'config.enabledOff': 'Disabled',
    'config.host': 'Environment',
    'config.hostProd': 'Production',
    'config.hostStaging': 'Staging',
    'config.credentials': 'Credentials',
    'config.credentialsOk': 'Configured',
    'config.credentialsMissing': 'Missing',
    'config.disabledHint': 'Paraşüt connection is off; you can still enter mapping IDs manually. Save/delete on this page work normally while the connection is off — only the "pick from Paraşüt" catalog list is unavailable.',

    'tabs.courses': 'Course Mapping',
    'tabs.banks': 'Bank/Cash Mapping',
    'tabs.outbox': 'Invoice Queue',

    'courses.onlyUnmapped': 'Unmapped only',
    'courses.empty.title': 'No courses found',
    'courses.empty.desc': 'There is nothing to list.',
    'courses.mapped': 'Mapped',
    'courses.unmapped': 'Unmapped',
    'courses.inputLabel': 'Paraşüt product ID',
    'courses.inputPlaceholder': 'e.g. 12345678',
    'courses.save': 'Save',
    'courses.saving': 'Saving…',
    'courses.delete': 'Remove mapping',
    'courses.deleting': 'Removing…',
    'courses.requiredError': 'Paraşüt product ID is required.',
    'courses.saveSuccess': 'Course mapping saved.',
    'courses.deleteSuccess': 'Course mapping removed.',
    'courses.pick': 'Pick from Paraşüt',
    'courses.pickClose': 'Close',

    // Round 2 (2026-09-29) — general "education payment" product mapping.
    // Backend endpoint (GET/PUT /admin/parasut/invoice-item-mapping) verified
    // READY in round 3 — see api/admin/parasutMapping.js.
    'invoiceItem.title': 'Education Payment (General Product)',
    'invoiceItem.desc': 'The GENERAL product/service sent to Paraşüt when there is no course-specific mapping (or for installment-free/bare invoice rows).',
    'invoiceItem.notConfigured': 'Could not load the education payment mapping. Please refresh the page.',
    'invoiceItem.mappedTo': 'Selected product',
    'invoiceItem.unmapped': 'Not selected',
    'invoiceItem.inputLabel': 'Paraşüt product ID',
    'invoiceItem.inputPlaceholder': 'e.g. 12345678',
    'invoiceItem.save': 'Save',
    'invoiceItem.saving': 'Saving…',
    'invoiceItem.requiredError': 'Paraşüt product ID is required.',
    'invoiceItem.saveSuccess': 'Education payment product saved.',
    'invoiceItem.pick': 'Pick from Paraşüt',

    'banks.empty.title': 'No bank/cash accounts found',
    'banks.empty.desc': 'There is nothing to list.',
    'banks.type': 'Account type',
    'banks.currency': 'Currency',
    'banks.mappedTo': 'Paraşüt account',
    'banks.unmapped': 'Unmapped',
    'banks.inputLabel': 'Paraşüt account ID',
    'banks.inputPlaceholder': 'e.g. 87654321',
    'banks.save': 'Map',
    'banks.saving': 'Mapping…',
    'banks.delete': 'Remove mapping',
    'banks.deleting': 'Removing…',
    'banks.requiredError': 'Paraşüt account ID is required.',
    'banks.saveSuccess': 'Bank/cash mapping saved.',
    'banks.deleteSuccess': 'Bank/cash mapping removed.',
    'banks.forceBtn': 'Map anyway',
    'banks.forcing': 'Mapping…',
    'banks.pick': 'Pick from Paraşüt',
    'banks.pickClose': 'Close',

    'remote.disabled': 'Paraşüt connection is off; you can enter the ID manually.',
    'remote.loading': 'Loading Paraşüt catalog…',
    'remote.error': 'Could not load the Paraşüt catalog.',
    'remote.empty': 'No records found.',
    'remote.use': 'Use this',
    'remote.loadMore': 'Load more',
    'remote.loadingMore': 'Loading…',

    'outbox.summaryTitle': 'Status summary',
    'outbox.blockedTitle': 'Blocked rows ("what\'s pending")',
    'outbox.filterAll': 'All',

    // Round 6 (manual test findings) — technical value -> friendly label.
    'entity.sales_invoice': 'Sales invoice',
    'entity.contact': 'Contact (student)',
    'entity.payment': 'Collection',
    'entity.product': 'Product',
    'entity.e_document': 'E-document',

    'status.pending': 'Waiting to be sent',
    'status.in_progress': 'Sending',
    'status.awaiting_job': 'Paraşüt is processing',
    'status.done': 'Completed',
    'status.failed': 'Error',
    'status.blocked': 'Waiting on a previous step',
    'status.needs_verification': 'Needs review',
    'status.unknown': 'Status unclear',
    'status.cancelled': 'Cancelled',

    'operation.create': 'Create',
    'operation.update': 'Update',
    'operation.collect': 'Collect',
    'operation.officialize': 'Officialize',
    'operation.cancel': 'Cancel',

    'blockReason.missing_product_mapping': 'Missing product mapping',
    'blockReason.missing_account_mapping': 'Missing account mapping',
    'blockReason.missing_tax_number': "Missing student's national ID",
    'blockReason.depends_on': 'Waiting on a previous step',
    'outbox.empty': 'No blocked rows for this filter.',
    'outbox.refresh': 'Refresh',
    'outbox.col.entity': 'Record',
    'outbox.col.operation': 'Operation',
    'outbox.col.reason': 'Reason',
    'outbox.col.created': 'Created',

    // Round 2 (2026-09-29) — Outbox general queue (status filter, retry/resolve)
    'outbox.list.title': 'General Queue',
    'outbox.list.filterLabel': 'Status',
    'outbox.list.filterAll': 'All',
    'outbox.list.entityFilterLabel': 'Record type',
    'outbox.list.entityFilterAll': 'All',
    'outbox.list.empty': 'No records for this filter.',
    'outbox.list.col.status': 'Status',
    'outbox.list.retryBtn': 'Retry',
    'outbox.list.retryNoteLabel': 'Note (required)',
    'outbox.list.retryNoteRequired': 'A note is required.',
    'outbox.list.retrySuccess': 'Resent.',
    'outbox.list.resolveBtn': 'Resolve',
    'outbox.list.resolveTitleNeedsVerification': 'Verification result',
    'outbox.list.resolveTitleUnknown': 'Resolve status',
    'outbox.list.resolutionLabel': 'Resolution',
    'outbox.list.resolutionRequired': 'Please select a resolution.',
    'outbox.list.resolutionPending': 'Pending',
    'outbox.list.resolutionDone': 'Done',
    'outbox.list.resolutionFailed': 'Failed',
    'outbox.list.parasutIdLabel': 'Paraşüt Invoice ID',
    'outbox.list.parasutIdRequired': 'Paraşüt Invoice ID is required.',
    'outbox.list.doubleInvoiceWarning': 'Warning: retrying by mistake can create a DUPLICATE invoice in Paraşüt. Before continuing, verify in the Paraşüt panel that no invoice exists for this collection.',
    'outbox.list.confirmNotInParasut': 'I checked the Paraşüt panel and confirmed NO invoice exists for this collection.',
    'outbox.list.confirmNotInParasutRequired': 'You must check this confirmation before continuing.',
    'outbox.list.noteRequired': 'A note is required.',
    'outbox.list.resolveSuccess': 'Resolved.',
    'outbox.list.submit': 'Submit',

    'common.loading': 'Loading…',
    'common.cancel': 'Cancel',
    'common.retry': 'Retry',
  },
};

function useL() {
  const { lang } = useLanguage();
  const dict = STRINGS[lang] || STRINGS.en;
  return (key) => dict[key] || STRINGS.en[key] || key;
}

/**
 * Tur 6 — `L(key)` bulunamayan bir anahtarda anahtarın KENDİSİNİ döner (çirkin
 * ham metin, ör. "status.something_new"). Outbox teknik değerlerini (status/
 * entity_type/operation/block_reason) göstermek için bu YETERSİZ — bilinmeyen/
 * yeni bir backend değeri gelirse HAM DEĞERİN KENDİSİ (ör. "something_new")
 * gösterilmeli, anahtar string'i DEĞİL. `labelOr` bu ayrımı yapar.
 */
function labelOr(L, key, fallback) {
  const val = L(key);
  return val === key ? fallback : val;
}

const BLOCK_REASONS = ['', 'missing_product_mapping', 'missing_account_mapping', 'missing_tax_number', 'depends_on'];

const OUTBOX_STATUS_VARIANT = {
  done: 'success',
  pending: 'secondary',
  in_progress: 'secondary',
  awaiting_job: 'secondary',
  needs_verification: 'warning',
  failed: 'danger',
  blocked: 'danger',
  unknown: 'outline',
  cancelled: 'outline',
};

const ACCOUNT_TYPE_VARIANT = {
  bank: 'default',
  cash: 'success',
  pos: 'warning',
};

// Tur 2 — Outbox genel kuyruk durum/entity_type filtre seçenekleri
// (backend services/parasut/outbox.py VALID_STATUSES/ENTITY_TYPES ile
// BİREBİR, salt okundu bu turda).
const OUTBOX_STATUSES = [
  '', 'pending', 'in_progress', 'awaiting_job', 'done', 'failed',
  'blocked', 'unknown', 'needs_verification', 'cancelled',
];
const OUTBOX_ENTITY_TYPES = ['', 'contact', 'product', 'sales_invoice', 'payment', 'e_document'];

/**
 * Tur 6 (elle test bulguları) — outbox satırlarının teknik metinlerini
 * (entity_type/status/operation/block_reason) admin için anlaşılır TR/EN
 * etiketlere çevirir. Kod/anahtar/API adlarına DOKUNULMADI — yalnızca
 * GÖRÜNEN metin.
 */
const entityTypeLabel = (L, entityType) => labelOr(L, `entity.${entityType}`, entityType || '—');
const outboxStatusLabel = (L, status) => labelOr(L, `status.${status}`, status || '—');
const operationLabel = (L, operation) => labelOr(L, `operation.${operation}`, operation || '—');
const blockReasonLabel = (L, blockReason) => labelOr(L, `blockReason.${blockReason}`, blockReason || '—');

/**
 * Bir outbox satırının "kimlik" görünümü — ⚠️ backend `student_name`/`amount`/
 * `payment_ids` alanlarını bu turda HENÜZ EKLEMEDİ (kontrata göre bağlandı,
 * salt okumada `_OUTBOX_LIST_PROJECTION`'da yok). Alanlar VARSA öğrenci adı +
 * tutar gösterilir, YOKSA sessizce ham `entity_id`'ye düşülür — uydurma yok.
 */
function outboxRowIdentity(row) {
  if (row.student_name) {
    const amountText = typeof row.amount === 'number' ? ` — ${fmtTRY(row.amount)}` : '';
    return `${row.student_name}${amountText}`;
  }
  return row.entity_id;
}

// ─── Sayfa ─────────────────────────────────────────────────────────────────────

export default function AdminParasutMapping() {
  const L = useL();
  // Tur 2 — `?tab=outbox` ile derin link desteği: parasutStatusCell.jsx'in
  // "Outbox'a git" linki buraya doğrudan doğru sekmeyle düşer. Diğer
  // sekmeler (`courses`/`banks`) için davranış DEĞİŞMEDİ (varsayılan hâlâ
  // 'courses' — geriye dönük uyumlu). Bilerek `useSearchParams` (react-router)
  // YERİNE `window.location.search` — bu sayfa bazı testlerde `<Router>`
  // context'i OLMADAN render ediliyor (bkz. AdminParasutMapping.test.jsx),
  // salt-okunur ilk değer için Router bağımlılığı gereksiz risk.
  const initialTabParam = new URLSearchParams(window.location.search).get('tab');
  const initialTab = ['courses', 'banks', 'outbox'].includes(initialTabParam) ? initialTabParam : 'courses';
  const [tab, setTab] = useState(initialTab);

  return (
    <AdminLayout>
      <PageHeader title={L('page.title')} subtitle={L('page.subtitle')} />

      <ConfigBanner />

      <div className="mb-4 mt-6 flex gap-2 border-b" role="tablist">
        {[
          { id: 'courses', label: L('tabs.courses') },
          { id: 'banks', label: L('tabs.banks') },
          { id: 'outbox', label: L('tabs.outbox') },
        ].map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={tab === s.id}
            data-testid={`parasut-tab-${s.id}`}
            onClick={() => setTab(s.id)}
            className={`px-4 py-2 text-sm ${
              tab === s.id
                ? 'border-b-2 border-primary font-medium text-primary'
                : 'text-muted-foreground'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {tab === 'courses' && <CourseMappingsTab />}
      {tab === 'banks' && <BankMappingsTab />}
      {tab === 'outbox' && <OutboxTab />}
    </AdminLayout>
  );
}

// ─── Durum banner'ı ───────────────────────────────────────────────────────────

function ConfigBanner() {
  const L = useL();
  const { data, isLoading } = useParasutConfig();

  if (isLoading) return <LoadingState variant="bars" count={2} />;
  if (!data) return null;

  return (
    <div className="rounded-lg border p-4" data-testid="parasut-config-banner">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-medium">{L('config.title')}</h2>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="flex items-center justify-between rounded-md border px-3 py-2">
          <span className="text-sm text-muted-foreground">{L('config.enabled')}</span>
          <Badge variant={data.enabled ? 'success' : 'secondary'}>
            {data.enabled ? L('config.enabledOn') : L('config.enabledOff')}
          </Badge>
        </div>
        <div className="flex items-center justify-between rounded-md border px-3 py-2">
          <span className="text-sm text-muted-foreground">{L('config.host')}</span>
          <Badge variant="outline">
            {data.is_production_host ? L('config.hostProd') : L('config.hostStaging')}
          </Badge>
        </div>
        <div className="flex items-center justify-between rounded-md border px-3 py-2">
          <span className="text-sm text-muted-foreground">{L('config.credentials')}</span>
          <Badge variant={data.has_credentials ? 'success' : 'danger'}>
            {data.has_credentials ? L('config.credentialsOk') : L('config.credentialsMissing')}
          </Badge>
        </div>
      </div>

      {!data.enabled && (
        <p
          className="mt-3 rounded-md bg-muted p-3 text-sm"
          data-testid="parasut-config-disabled-msg"
        >
          {L('config.disabledHint')}
        </p>
      )}
    </div>
  );
}

// ─── Paraşüt katalog seçici (ürün / hesap) ────────────────────────────────────

/**
 * Ortak "Paraşüt'ten seç" açılır paneli. `kind` 'product' | 'account'.
 * Sorgu yalnızca panel AÇILINCA (`enabled`) tetiklenir — sayfa yüklenirken
 * bayrak kapalıysa gereksiz 503 patlatmamak için.
 *
 * Tur 5 (staging bulgusu — Paraşüt `page[size]` sınırı 25) — sayfa boyutu
 * 25'e sabit, "Daha fazla yükle" ile sayfa sayfa biriktirilir. Backend yanıtı
 * (`{"data": [...]}`) sayfalama METADATA'sı TAŞIMAZ (salt okundu, bkz.
 * api/admin/parasutMapping.js Tur 5 notu) — bu yüzden "daha fazla var mı"
 * kararı UYDURULMADI, sezgisel bir kurala dayanır: son çekilen sayfa TAM
 * `PAGE_SIZE` (25) kayıt döndürdüyse büyük ihtimalle daha fazlası vardır.
 * Son sayfa 25'ten AZ döndürürse liste biter (buton kaybolur).
 */
const PARASUT_REMOTE_PAGE_SIZE = 25;

function RemotePicker({ kind, onPick, onClose }) {
  const L = useL();
  const [pageNumber, setPageNumber] = useState(1);
  const [accumulatedItems, setAccumulatedItems] = useState([]);
  const lastAppendedPageRef = useRef(0);

  const products = useParasutRemoteProducts(kind === 'product', PARASUT_REMOTE_PAGE_SIZE, pageNumber);
  const accounts = useParasutRemoteAccounts(kind === 'account', PARASUT_REMOTE_PAGE_SIZE, pageNumber);
  const { data, isLoading, isFetching, isError, error } = kind === 'product' ? products : accounts;

  const pageItems = data?.data || [];

  // Yeni sayfa geldiğinde bir öncekinin ÜSTÜNE ekle (aynı sayfa iki kez
  // eklenmesin diye `lastAppendedPageRef` ile korunur — cache'ten tekrar
  // aynı veri dönerse StrictMode/yeniden render'da çift eklemeyi önler).
  useEffect(() => {
    if (data && lastAppendedPageRef.current !== pageNumber) {
      lastAppendedPageRef.current = pageNumber;
      if (pageItems.length > 0) {
        setAccumulatedItems((prev) => [...prev, ...pageItems]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pageNumber]);

  const items = accumulatedItems;
  const is503 = error?.response?.status === 503;
  const canLoadMore = !isError && pageItems.length === PARASUT_REMOTE_PAGE_SIZE;
  const showInitialLoading = isLoading && pageNumber === 1;

  return (
    <div className="mt-2 rounded-md border bg-muted/30 p-3" data-testid="parasut-remote-picker">
      {showInitialLoading && <p className="text-sm text-muted-foreground">{L('remote.loading')}</p>}

      {isError && is503 && (
        <p className="text-sm" data-testid="parasut-remote-disabled-msg">
          {L('remote.disabled')}
        </p>
      )}

      {isError && !is503 && (
        <p className="text-sm text-destructive" role="alert">{L('remote.error')}</p>
      )}

      {!showInitialLoading && !isError && items.length === 0 && (
        <p className="text-sm text-muted-foreground">{L('remote.empty')}</p>
      )}

      {!showInitialLoading && items.length > 0 && (
        <ul className="max-h-56 space-y-1 overflow-y-auto">
          {items.map((item) => {
            const attrs = item?.attributes || {};
            return (
              <li key={item.id} className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-muted">
                <span className="min-w-0 truncate text-sm">
                  {attrs.name || item.id}
                  {attrs.currency ? ` · ${attrs.currency}` : ''}
                  <span className="ml-1 text-xs text-muted-foreground">#{item.id}</span>
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid={`parasut-remote-pick-${item.id}`}
                  onClick={() => onPick(String(item.id), attrs.name)}
                >
                  {L('remote.use')}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {!showInitialLoading && items.length > 0 && canLoadMore && (
        <div className="mt-2 text-center">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isFetching}
            onClick={() => setPageNumber((p) => p + 1)}
            data-testid="parasut-remote-load-more"
          >
            {isFetching ? L('remote.loadingMore') : L('remote.loadMore')}
          </Button>
        </div>
      )}

      <div className="mt-2 text-right">
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          {L('courses.pickClose')}
        </Button>
      </div>
    </div>
  );
}

// ─── "Eğitim Ödemesi" genel ürün eşlemesi (Tur 2, backend Tur 3'te doğrulandı) ──

/**
 * Kurs bazlı eşlemenin ALTERNATİFİ/tamamlayıcısı — havale/kart tahsilatı
 * hangi kursla eşleşirse eşleşsin, kurs eşlemesi yoksa kullanılacak GENEL
 * ürün. Backend ucu (GET/PUT /admin/parasut/invoice-item-mapping) Tur 3'te
 * HAZIR olarak doğrulandı (salt okundu) — eşleme yoksa GET 200 + gövde
 * `null` döner (normal, hata DEĞİL). `useInvoiceItemMapping`'in `isError`
 * dalı yalnızca GERÇEK bir ağ/sunucu hatasında tetiklenir (savunma amaçlı,
 * bkz. api/admin/parasutMapping.js) — kart bu durumda `notConfigured`
 * mesajını gösterir, ÇÖKMEZ.
 */
function InvoiceItemMappingCard() {
  const L = useL();
  const toast = useToast();
  const { data, isLoading, isError } = useInvoiceItemMapping();
  const [value, setValue] = useState('');
  const [productName, setProductName] = useState('');
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const setMapping = useSetInvoiceItemMapping();

  const mapped = data?.parasut_product_id ? data : null;

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(L('invoiceItem.requiredError'));
      return;
    }
    setError('');
    try {
      await setMapping.mutateAsync({ parasutProductId: trimmed, parasutProductName: productName.trim() || undefined });
      toast.success(L('invoiceItem.saveSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="rounded-lg border p-4" data-testid="parasut-invoice-item-mapping-card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium">{L('invoiceItem.title')}</div>
          <div className="text-xs text-muted-foreground">{L('invoiceItem.desc')}</div>
        </div>
        {!isLoading && !isError && (
          <Badge variant={mapped ? 'success' : 'outline'}>
            {mapped ? L('invoiceItem.mappedTo') : L('invoiceItem.unmapped')}
          </Badge>
        )}
      </div>

      {isLoading && <LoadingState variant="bars" count={1} />}

      {!isLoading && isError && (
        <p className="text-sm text-muted-foreground" data-testid="parasut-invoice-item-not-configured">
          {L('invoiceItem.notConfigured')}
        </p>
      )}

      {!isLoading && !isError && (
        <>
          {mapped && (
            <p className="mb-2 text-xs text-muted-foreground">
              {L('invoiceItem.mappedTo')}: {mapped.parasut_product_name || '—'} (#{mapped.parasut_product_id})
            </p>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm">
              <span className="mb-1 block font-medium">{L('invoiceItem.inputLabel')}</span>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={L('invoiceItem.inputPlaceholder')}
                className="w-48 rounded-md border px-3 py-2 text-sm"
                data-testid="parasut-invoice-item-input"
              />
            </label>
            <Button
              type="button"
              size="sm"
              onClick={save}
              disabled={setMapping.isPending}
              data-testid="parasut-invoice-item-save"
            >
              {setMapping.isPending ? L('invoiceItem.saving') : L('invoiceItem.save')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setPickerOpen((v) => !v)}
              data-testid="parasut-invoice-item-pick-toggle"
            >
              {L('invoiceItem.pick')} {pickerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Button>
          </div>
          {error && (
            <p className="mt-2 text-sm text-destructive" role="alert" data-testid="parasut-invoice-item-error">
              {error}
            </p>
          )}
          {pickerOpen && (
            <RemotePicker
              kind="product"
              onPick={(id, name) => { setValue(id); setProductName(name || ''); setPickerOpen(false); }}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── Kurs eşleme sekmesi ──────────────────────────────────────────────────────

function CourseMappingsTab() {
  const L = useL();
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const { data, isLoading } = useCourseMappings(onlyUnmapped);

  if (isLoading) return <LoadingState />;
  const courses = data?.courses || [];

  return (
    <div className="space-y-3">
      <InvoiceItemMappingCard />

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={onlyUnmapped}
          onChange={(e) => setOnlyUnmapped(e.target.checked)}
          data-testid="parasut-courses-only-unmapped"
        />
        {L('courses.onlyUnmapped')}
      </label>

      {courses.length === 0 ? (
        <EmptyState title={L('courses.empty.title')} description={L('courses.empty.desc')} />
      ) : (
        <div className="space-y-2">
          {courses.map((course) => (
            <CourseMappingRow key={course.course_id} course={course} />
          ))}
        </div>
      )}
    </div>
  );
}

function CourseMappingRow({ course }) {
  const L = useL();
  const toast = useToast();
  const [value, setValue] = useState(course.parasut_product_id || '');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const setMapping = useSetCourseMapping();
  const deleteMapping = useDeleteCourseMapping();

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(L('courses.requiredError'));
      return;
    }
    setError('');
    try {
      await setMapping.mutateAsync({ courseId: course.course_id, data: { parasut_product_id: trimmed, note } });
      toast.success(L('courses.saveSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const remove = async () => {
    try {
      await deleteMapping.mutateAsync(course.course_id);
      setValue('');
      toast.success(L('courses.deleteSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="rounded-lg border p-4" data-testid={`parasut-course-row-${course.course_id}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium">{course.title || course.course_id}</div>
          <div className="text-xs text-muted-foreground">{course.course_id}</div>
        </div>
        <Badge variant={course.parasut_product_id ? 'success' : 'outline'}>
          {course.parasut_product_id ? L('courses.mapped') : L('courses.unmapped')}
        </Badge>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="mb-1 block font-medium">{L('courses.inputLabel')}</span>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={L('courses.inputPlaceholder')}
            className="w-48 rounded-md border px-3 py-2 text-sm"
            data-testid={`parasut-course-input-${course.course_id}`}
          />
        </label>

        <Button
          type="button"
          size="sm"
          onClick={save}
          disabled={setMapping.isPending}
          data-testid={`parasut-course-save-${course.course_id}`}
        >
          {setMapping.isPending ? L('courses.saving') : L('courses.save')}
        </Button>

        {course.parasut_product_id && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={remove}
            disabled={deleteMapping.isPending}
            data-testid={`parasut-course-delete-${course.course_id}`}
          >
            {deleteMapping.isPending ? L('courses.deleting') : L('courses.delete')}
          </Button>
        )}

        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setPickerOpen((v) => !v)}
          data-testid={`parasut-course-pick-toggle-${course.course_id}`}
        >
          {L('courses.pick')} {pickerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </Button>
      </div>

      {error && (
        <p
          className="mt-2 text-sm text-destructive"
          role="alert"
          data-testid={`parasut-course-error-${course.course_id}`}
        >
          {error}
        </p>
      )}

      {pickerOpen && (
        <RemotePicker
          kind="product"
          onPick={(id) => { setValue(id); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Banka/kasa eşleme sekmesi ────────────────────────────────────────────────

function BankMappingsTab() {
  const L = useL();
  const { data, isLoading } = useBankMappings();

  if (isLoading) return <LoadingState />;
  const banks = data?.banks || [];

  if (banks.length === 0) {
    return <EmptyState title={L('banks.empty.title')} description={L('banks.empty.desc')} />;
  }

  return (
    <div className="space-y-2">
      {banks.map((bank) => (
        <BankMappingRow key={bank.bank_id} bank={bank} />
      ))}
    </div>
  );
}

function BankMappingRow({ bank }) {
  const L = useL();
  const toast = useToast();
  const [value, setValue] = useState(bank.mapping?.parasut_account_id || '');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  // Tur 6 — {message} yeterli, `local_currency`/`parasut_currency` JSX'te
  // AYRICA gösterilmiyor (mesaj zaten backend'in message_tr/message_en'i,
  // bu alanları metne gömüyor) — bu yüzden `account_type` uyuşmazlığı da
  // (⚠️ backend'de bu turda HENÜZ YOK, kontrata göre bağlandı — error_code
  // `hesap_turu_uyusmuyor`) AYNI state/aynı "yine de eşle" akışını sorunsuz
  // paylaşır, ek alan eklemeye gerek YOK.
  const [mismatch, setMismatch] = useState(null); // {message}
  const [pickerOpen, setPickerOpen] = useState(false);

  const setMapping = useSetBankMapping();
  const deleteMapping = useDeleteBankMapping();

  const submit = async (force = false) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(L('banks.requiredError'));
      return;
    }
    setError('');
    try {
      await setMapping.mutateAsync({
        bankId: bank.bank_id,
        data: { parasut_account_id: trimmed, note, force },
      });
      setMismatch(null);
      toast.success(L('banks.saveSuccess'));
    } catch (err) {
      // Tur 6 — `hesap_turu_uyusmuyor` (⚠️ backend'de bu turda HENÜZ YOK,
      // kontrata göre bağlandı) `currency_mismatch` İLE AYNI "409 + force
      // ile yeniden dene" akışını paylaşır — backend'in kendi message_tr/
      // message_en'i zaten anlaşılır, burada özel bir metin ÜRETİLMEDİ.
      const code = getApiErrorCode(err);
      if (code === 'currency_mismatch' || code === 'hesap_turu_uyusmuyor') {
        setMismatch({ message: getApiErrorMessage(err) });
        return;
      }
      toast.error(getApiErrorMessage(err));
    }
  };

  const remove = async () => {
    try {
      await deleteMapping.mutateAsync(bank.bank_id);
      setValue('');
      setMismatch(null);
      toast.success(L('banks.deleteSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="rounded-lg border p-4" data-testid={`parasut-bank-row-${bank.bank_id}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium">{bank.name || bank.bank_id}</div>
          <div className="text-xs text-muted-foreground">{bank.bank_id}</div>
        </div>
        <div className="flex flex-wrap gap-1">
          <Badge variant={ACCOUNT_TYPE_VARIANT[bank.account_type] || 'outline'}>
            {bank.account_type || '—'}
          </Badge>
          <Badge variant="outline">{bank.currency || 'TRY'}</Badge>
          <Badge variant={bank.mapping ? 'success' : 'outline'}>
            {bank.mapping ? L('banks.mappedTo') : L('banks.unmapped')}
          </Badge>
        </div>
      </div>

      {bank.mapping && (
        <p className="mb-2 text-xs text-muted-foreground">
          {L('banks.mappedTo')}: #{bank.mapping.parasut_account_id}
          {bank.mapping.parasut_currency ? ` (${bank.mapping.parasut_currency})` : ''}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="mb-1 block font-medium">{L('banks.inputLabel')}</span>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={L('banks.inputPlaceholder')}
            className="w-48 rounded-md border px-3 py-2 text-sm"
            data-testid={`parasut-bank-input-${bank.bank_id}`}
          />
        </label>

        <Button
          type="button"
          size="sm"
          onClick={() => submit(false)}
          disabled={setMapping.isPending}
          data-testid={`parasut-bank-save-${bank.bank_id}`}
        >
          {setMapping.isPending ? L('banks.saving') : L('banks.save')}
        </Button>

        {bank.mapping && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={remove}
            disabled={deleteMapping.isPending}
            data-testid={`parasut-bank-delete-${bank.bank_id}`}
          >
            {deleteMapping.isPending ? L('banks.deleting') : L('banks.delete')}
          </Button>
        )}

        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setPickerOpen((v) => !v)}
          data-testid={`parasut-bank-pick-toggle-${bank.bank_id}`}
        >
          {L('banks.pick')} {pickerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </Button>
      </div>

      {error && (
        <p
          className="mt-2 text-sm text-destructive"
          role="alert"
          data-testid={`parasut-bank-error-${bank.bank_id}`}
        >
          {error}
        </p>
      )}

      {mismatch && (
        <div
          className="mt-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
          role="alert"
          data-testid={`parasut-bank-mismatch-${bank.bank_id}`}
        >
          <p className="flex items-start gap-2 text-destructive">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{mismatch.message}</span>
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => submit(true)}
            disabled={setMapping.isPending}
            data-testid={`parasut-bank-force-${bank.bank_id}`}
          >
            {setMapping.isPending ? L('banks.forcing') : L('banks.forceBtn')}
          </Button>
        </div>
      )}

      {pickerOpen && (
        <RemotePicker
          kind="account"
          onPick={(id) => { setValue(id); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Outbox sekmesi ───────────────────────────────────────────────────────────

function OutboxTab() {
  const L = useL();
  const summary = useOutboxSummary();
  const [reason, setReason] = useState('');
  const blocked = useOutboxBlocked(reason);

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-medium">{L('outbox.summaryTitle')}</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => summary.refetch()}
            data-testid="parasut-outbox-refresh"
          >
            <RefreshCw size={14} className="mr-1" aria-hidden="true" />
            {L('outbox.refresh')}
          </Button>
        </div>

        {summary.isLoading && <LoadingState variant="bars" count={3} />}

        {!summary.isLoading && summary.data && (
          <div className="flex flex-wrap gap-2" data-testid="parasut-outbox-summary">
            {Object.entries(summary.data.counts || {}).map(([status, count]) => (
              <Badge
                key={status}
                variant={OUTBOX_STATUS_VARIANT[status] || 'outline'}
                data-testid={`parasut-outbox-badge-${status}`}
              >
                {outboxStatusLabel(L, status)}: {count}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-2 font-medium">{L('outbox.blockedTitle')}</h3>

        <div className="mb-2 flex flex-wrap gap-2">
          {BLOCK_REASONS.map((r) => (
            <Button
              key={r || 'all'}
              type="button"
              size="sm"
              variant={reason === r ? 'default' : 'outline'}
              onClick={() => setReason(r)}
              data-testid={`parasut-outbox-filter-${r || 'all'}`}
            >
              {r === '' ? L('outbox.filterAll') : blockReasonLabel(L, r)}
            </Button>
          ))}
        </div>

        {blocked.isLoading && <LoadingState variant="table" rows={3} />}

        {!blocked.isLoading && (blocked.data?.rows || []).length === 0 && (
          <EmptyState title={L('outbox.empty')} />
        )}

        {!blocked.isLoading && (blocked.data?.rows || []).length > 0 && (
          <div className="space-y-2" data-testid="parasut-outbox-blocked-list">
            {(blocked.data.rows || []).map((row) => (
              <div
                key={row.outbox_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-3"
                data-testid={`parasut-outbox-blocked-row-${row.outbox_id}`}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {entityTypeLabel(L, row.entity_type)} · {outboxRowIdentity(row)}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {operationLabel(L, row.operation)} · {(row.created_at || '').slice(0, 10)}
                  </div>
                </div>
                <Badge variant="danger">{blockReasonLabel(L, row.block_reason)}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      <OutboxGeneralSection />
    </div>
  );
}

// ─── Outbox genel kuyruk (Tur 2) — durum filtreli, retry/resolve aksiyonları ──

/**
 * `useOutboxList`/`useRetryOutboxItem`/`useResolveNeedsVerification`/
 * `useResolveUnknown` — HAZIR backend uçları (routes/parasut_admin.py "FAZ 4",
 * salt okundu bu turda). Bu bölüm `outbox.blockedTitle` (yalnızca `blocked`)
 * bölümünden AYRI: TÜM durumları (status filtreli) gösterir ve `failed`/
 * `needs_verification`/`unknown` için aksiyon sunar — `parasutStatusCell.jsx`
 * (Borç & Tahsilat ekranı) "Outbox'a git" linki buraya düşer.
 */
function OutboxGeneralSection() {
  const L = useL();
  const [status, setStatus] = useState('');
  const [entityType, setEntityType] = useState('');
  const { data, isLoading, isFetching, refetch } = useOutboxList(status, entityType);

  const rows = data?.rows || [];

  return (
    <div data-testid="parasut-outbox-general-section">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-medium">{L('outbox.list.title')}</h3>
        <Button type="button" size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} data-testid="parasut-outbox-list-refresh">
          <RefreshCw size={14} className="mr-1" aria-hidden="true" />
          {L('outbox.refresh')}
        </Button>
      </div>

      <div className="mb-3 flex flex-wrap gap-3">
        <label className="text-sm">
          <span className="mb-1 block font-medium">{L('outbox.list.filterLabel')}</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            data-testid="parasut-outbox-list-status-filter"
          >
            {OUTBOX_STATUSES.map((s) => (
              <option key={s || 'all'} value={s}>{s ? outboxStatusLabel(L, s) : L('outbox.list.filterAll')}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium">{L('outbox.list.entityFilterLabel')}</span>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            data-testid="parasut-outbox-list-entity-filter"
          >
            {OUTBOX_ENTITY_TYPES.map((e) => (
              <option key={e || 'all'} value={e}>{e ? entityTypeLabel(L, e) : L('outbox.list.entityFilterAll')}</option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && <LoadingState variant="table" rows={3} />}

      {!isLoading && rows.length === 0 && <EmptyState title={L('outbox.list.empty')} />}

      {!isLoading && rows.length > 0 && (
        <div className="space-y-2" data-testid="parasut-outbox-list">
          {rows.map((row) => (
            <OutboxListRow key={row.outbox_id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function OutboxListRow({ row }) {
  const L = useL();
  const toast = useToast();
  const [actionOpen, setActionOpen] = useState(false);
  const [note, setNote] = useState('');
  const [resolution, setResolution] = useState('');
  const [parasutId, setParasutId] = useState('');
  const [confirmedNotInParasut, setConfirmedNotInParasut] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const retry = useRetryOutboxItem();
  const resolveNeedsVerification = useResolveNeedsVerification();
  const resolveUnknown = useResolveUnknown();

  const isFailed = row.status === 'failed';
  const isNeedsVerification = row.status === 'needs_verification';
  const isUnknown = row.status === 'unknown';
  const canAct = isFailed || isNeedsVerification || isUnknown;
  const busy = retry.isPending || resolveNeedsVerification.isPending || resolveUnknown.isPending;

  // Kontrat güncellemesi (aynı gün, koordinatör mesajı — backend bu turda
  // HENÜZ YAZIYOR, salt okumada resolve_needs_verification_manual bu iki
  // kuralı henüz zorlamıyordu): "pending" seçilince çift fatura riskine
  // karşı açık onay checkbox'ı; "done" seçilince Paraşüt ID zorunlu. Bu iki
  // kural yalnızca `needs_verification` diyaloğuna uygulanır — `resolve_
  // unknown_manual` (salt okundu) `parasut_id` parametresi TAŞIMIYOR, bu
  // yüzden Unknown akışında ek alan/zorunluluk EKLENMEDİ (uydurulmadı).
  const isPendingResolution = isNeedsVerification && resolution === 'pending';
  const isDoneResolution = isNeedsVerification && resolution === 'done';

  const resolutionOptions = isNeedsVerification
    ? [
        { value: 'pending', label: L('outbox.list.resolutionPending') },
        { value: 'done', label: L('outbox.list.resolutionDone') },
        { value: 'failed', label: L('outbox.list.resolutionFailed') },
      ]
    : [
        { value: 'done', label: L('outbox.list.resolutionDone') },
        { value: 'failed', label: L('outbox.list.resolutionFailed') },
      ];

  const submit = async () => {
    setSubmitted(true);
    const trimmedNote = note.trim();
    if (!trimmedNote) return;
    if (isPendingResolution && !confirmedNotInParasut) return;
    if (isDoneResolution && !parasutId.trim()) return;
    try {
      if (isFailed) {
        await retry.mutateAsync({ outboxId: row.outbox_id, note: trimmedNote });
        toast.success(L('outbox.list.retrySuccess'));
      } else if (isNeedsVerification) {
        if (!resolution) return;
        await resolveNeedsVerification.mutateAsync({
          outboxId: row.outbox_id,
          resolution,
          note: trimmedNote,
          parasutId: parasutId.trim() || undefined,
          confirmedNotInParasut,
        });
        toast.success(L('outbox.list.resolveSuccess'));
      } else if (isUnknown) {
        if (!resolution) return;
        await resolveUnknown.mutateAsync({ outboxId: row.outbox_id, resolution, note: trimmedNote });
        toast.success(L('outbox.list.resolveSuccess'));
      }
      setActionOpen(false);
      setNote('');
      setResolution('');
      setParasutId('');
      setConfirmedNotInParasut(false);
      setSubmitted(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="rounded-lg border p-3" data-testid={`parasut-outbox-list-row-${row.outbox_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-sm">
            {entityTypeLabel(L, row.entity_type)} · {outboxRowIdentity(row)}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {operationLabel(L, row.operation)} · {(row.created_at || '').slice(0, 10)}
            {row.attempts != null && row.max_attempts != null ? ` · ${row.attempts}/${row.max_attempts}` : ''}
          </div>
          {row.last_error && (
            <div className="mt-1 truncate text-xs text-destructive" title={row.last_error}>
              {row.last_error}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={OUTBOX_STATUS_VARIANT[row.status] || 'outline'}>{outboxStatusLabel(L, row.status)}</Badge>
          {canAct && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setActionOpen((v) => !v)}
              data-testid={`parasut-outbox-list-action-toggle-${row.outbox_id}`}
            >
              {isFailed ? L('outbox.list.retryBtn') : L('outbox.list.resolveBtn')}
            </Button>
          )}
        </div>
      </div>

      {actionOpen && (
        <div className="mt-3 space-y-2 border-t pt-3">
          {!isFailed && (
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{L('outbox.list.resolutionLabel')}</span>
              <select
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                data-testid={`parasut-outbox-resolution-select-${row.outbox_id}`}
              >
                <option value="">—</option>
                {resolutionOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {submitted && !resolution && (
                <p className="mt-1 text-sm text-destructive" role="alert">{L('outbox.list.resolutionRequired')}</p>
              )}
            </label>
          )}
          {isDoneResolution && (
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{L('outbox.list.parasutIdLabel')} *</span>
              <input
                type="text"
                value={parasutId}
                onChange={(e) => setParasutId(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                data-testid={`parasut-outbox-parasut-id-input-${row.outbox_id}`}
              />
              {submitted && !parasutId.trim() && (
                <p className="mt-1 text-sm text-destructive" role="alert">{L('outbox.list.parasutIdRequired')}</p>
              )}
            </label>
          )}
          {isPendingResolution && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1.5">
              <p className="text-sm text-amber-800">{L('outbox.list.doubleInvoiceWarning')}</p>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirmedNotInParasut}
                  onChange={(e) => setConfirmedNotInParasut(e.target.checked)}
                  className="mt-0.5"
                  data-testid={`parasut-outbox-confirm-not-in-parasut-${row.outbox_id}`}
                />
                <span>{L('outbox.list.confirmNotInParasut')}</span>
              </label>
              {submitted && !confirmedNotInParasut && (
                <p className="text-sm text-destructive" role="alert">{L('outbox.list.confirmNotInParasutRequired')}</p>
              )}
            </div>
          )}
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{L('outbox.list.retryNoteLabel')}</span>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
              data-testid={`parasut-outbox-note-input-${row.outbox_id}`}
            />
            {submitted && !note.trim() && (
              <p className="mt-1 text-sm text-destructive" role="alert">{L('outbox.list.retryNoteRequired')}</p>
            )}
          </label>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={submit}
              disabled={busy}
              data-testid={`parasut-outbox-action-submit-${row.outbox_id}`}
            >
              {L('outbox.list.submit')}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setActionOpen(false)} disabled={busy}>
              {L('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
